import dayjs from 'dayjs'
import { cloneDeep, forEach } from 'lodash-es'
import { DictionaryItem } from '../../bewotec/openapi/models/DictionaryItem'
import { DictionaryType } from '../../bewotec/openapi/models/DictionaryType'
import { handleError } from '../../general/errorHandling'
import { generateRequestId, getLocalForage, globalHelper, ISO_DATE_FORMAT } from '../../general/helper'
import { PRICE_MODEL_SORT_ORDER } from '../../general/master-data'
import { appHelper } from '../app/helper'
import { useCatalogStore } from '../catalog/index'
import { resetCruise } from '../details/cruise/actions'
import { useCruiseListStore, useFilterStore, useSortStore, useUiStore } from '../index'
import { trackingHelper } from '../tracking/helper'
import { FilterSelectTrackingEventParams } from '../tracking/interface'
import defaultValues, { DefaultValues } from './defaultValues'

import {
  addExtraKeyWords,
  filterHelper,
  mapHolidays,
  parseFilterPreset,
  sortHolidays,
  sortValuesAlphabetically,
  sortValuesBySortOrder,
} from './helper.js'
import { FieldValue, FilterFieldDropDownType, IFilterFields, ITravelData, ResponseHoliday } from './interface'
import { validateFilterField } from './validate'

const localForage = getLocalForage()

export const stashFilterPreset = async () => {
  await localForage?.getItem('filterPreset', async (_error, value: string | null) => {
    if (!value && location.search && !location.search.includes('ticket')) {
      await localForage?.setItem('filterPreset', location.search)
    }
  })
}

export const presetFilterFields = async () => {
  const filterStore = useFilterStore()

  await localForage?.getItem('filterPreset', async (_error, value: string | null) => {
    if (!!value) {
      const filterPreset = parseFilterPreset(value)

      forEach(filterPreset, (fieldValue, fieldName) => {
        filterStore.setFilter(fieldName as keyof IFilterFields, fieldValue)
      })
    }
  })
  await localForage?.setItem('filterPreset', null)
}

export const setFilter = (fieldName: keyof IFilterFields, fieldValue: FieldValue) => {
  const filterStore = useFilterStore()

  if (!fieldValue) {
    filterStore.filterFields[fieldName].value = defaultValues[fieldName].value
  } else if (validateFilterField(filterStore.filterFields[fieldName], fieldValue)) {
    filterStore.filterFields[fieldName].value = fieldValue
  }

  filterHelper.updateQueryStrings(fieldName as keyof IFilterFields, fieldValue)
}

export const preselectFilterEvent = () => {
  const filterStore = useFilterStore()
  const catalogStore = useCatalogStore()

  const filterFields = filterStore.filterFields

  const hasDefaultFiltersSet = Object.entries(filterFields).every(([fieldName, fieldValue]) => {
    const defaultValues = new DefaultValues()
    const defaultValue = defaultValues[fieldName as keyof IFilterFields].value
    const selectedValue = fieldValue.value

    return Array.isArray(selectedValue) && Array.isArray(defaultValue)
      ? selectedValue.length === defaultValue.length
      : selectedValue === defaultValue
  })

  if (!hasDefaultFiltersSet && !!catalogStore.totalResults) {
    filterStore.addFilterSelectTrackingEvent()
  }
}

export const fetchFilters = async () => {
  const filterStore = useFilterStore()
  try {
    await Promise.all([
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.SHIP })
        .then(values => filterStore.mapFilter({ values, type: 'ships' })),
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.PRICEMODEL })
        .then(values => {
          const sortedValues = sortValuesBySortOrder(values, PRICE_MODEL_SORT_ORDER)
          filterStore.mapFilter({ values: sortedValues, type: 'priceModels' })
        }),
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.REGION })
        .then(values => {
          const sortedValues = sortValuesAlphabetically(values)
          filterStore.mapFilter({ values: sortedValues, type: 'regions' })
        }),
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.CAMPAIGN })
        .then(values => {
          const sortedValues = sortValuesAlphabetically(values)
          filterStore.mapFilter({ values: sortedValues, type: 'campaigns' })
        }),
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.CATEGORY_GROUP })
        .then(values => filterStore.mapFilter({ values, type: 'categories' })),
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.GATEWAY })
        .then(values => filterStore.mapFilter({ values, type: 'airports' })),
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.CRUISE_ATTRIBUTE })
        .then(values => filterStore.mapFilter({ values, type: 'cruiseAttributes' })),
      appHelper
        .bewotecMicroServiceApi()
        .findDictionaryItems({ type: DictionaryType.PORT })
        .then(values => filterStore.mapFilter({ values, type: 'ports' })),
    ])
  } catch (error) {
    handleError({ error, origin: 'fetchFilters' })
  }
}

export const mapFilter = ({ values, type }: { values: DictionaryItem[]; type: FilterFieldDropDownType }) => {
  const filterStore = useFilterStore()

  const mappedValues: ITravelData[] = values.map(value => {
    const extraKeyWords: string[] = addExtraKeyWords(type, value)

    return {
      key: value.code,
      keywords: !extraKeyWords.length ? [value.code, value.name] : [value.code, value.name].concat(extraKeyWords),
      display: type !== 'airports' ? value.name : `${value.name} (${value.code})`,
    }
  })

  filterStore.filterFieldDropDownData[type] = mappedValues
}

export const addFilterSelectTrackingEvent = () => {
  const filterStore = useFilterStore()
  const catalogStore = useCatalogStore()

  const filterFields = filterStore.filterFields
  const filterFieldDropDownData = filterStore.filterFieldDropDownData

  const params: FilterSelectTrackingEventParams = {
    selection: {
      departureDate: filterFields.departureDate.value ?? '',
      arrivalDate: filterFields.arrivalDate.value ?? '',
      duration: filterFields.duration.value,
      campaigns: filterFields.campaigns.value,
      regions: filterFields.regions.value,
      ports: portDisplayNamesFromPortCodes(filterFields.ports.value),
      isStartPort: filterFields.isStartPort.value,
      isEndPort: filterFields.isEndPort.value,
      cruiseAttributes: filterFields.cruiseAttributes.value,
      airports: filterFields.airports.value,
      hasFlexFlight: filterFields.hasFlexFlight.value,
      // TODO: check with tracking guys
      hasHigherFlightClasses: filterFields.hasHigherFlightClasses.value,
      ships: filterFields.ships.value,
      categories: filterFields.categories.value,
      priceModels: filterFields.priceModels.value,
      adults: filterFields.adults.value ?? '',
      juveniles: filterFields.juveniles.value ?? '',
      children: filterFields.children.value ?? '',
      babies: filterFields.babies.value ?? '',
    },
    data: filterFieldDropDownData,
    totalResults: catalogStore.totalResults,
  }

  const event = trackingHelper.buildFilterSelectTrackingEvent(params)
  trackingHelper.addTrackingEvent(event, 'Filter Select Tracking Event')
}

export const resetFilterSelectionAndRemoveQueryStrings = async () => {
  const sortStore = useSortStore()
  sortStore.resetSortOrder()

  const filterStore = useFilterStore()
  filterStore.filterFields = cloneDeep(defaultValues)

  globalHelper.deleteQueryStrings()
  await filterStore.resetFilterSelection()
}

export const resetFilterSelection = async () => {
  const uiStore = useUiStore()
  const cruiseListStore = useCruiseListStore()
  const catalogStore = useCatalogStore()

  uiStore.hideDetailsPanel()

  cruiseListStore.selectedEntryId = ''

  resetCruise()

  catalogStore.pageNumber = 1
  catalogStore.requestId = generateRequestId('FETCAT')

  await catalogStore.fetchCatalog()
  addFilterSelectTrackingEvent()
}

export const setHolidayCountrySelectedByStates = (countryCode: string) => {
  const filterStore = useFilterStore()

  const federalStates = filterStore.holidaysByCountry[countryCode].federalStates
  const value = Object.values(federalStates).every(state => state.selected)

  filterStore.holidaysByCountry[countryCode].selected = value
}

export const setHolidayStateSelectedByCountry = (countryCode: string, value: boolean) => {
  const filterStore = useFilterStore()
  const holidaysByCountry = filterStore.holidaysByCountry[countryCode]

  holidaysByCountry.selected = value

  for (const stateCode of Object.keys(holidaysByCountry.federalStates)) {
    holidaysByCountry.federalStates[stateCode].selected = value
  }
}

export const fetchHolidays = async () => {
  const filterStore = useFilterStore()

  try {
    const from = dayjs().format(ISO_DATE_FORMAT)
    const until = dayjs().add(3, 'years').add(-1, 'days').format(ISO_DATE_FORMAT)
    const countries = ['DE', 'AT', 'CH']
    filterStore.holidayError = false

    for (const country of countries) {
      const url = `https://openholidaysapi.org/SchoolHolidays?countryIsoCode=${country}&validFrom=${from}&validTo=${until}&languageIsoCode=DE`
      const options: RequestInit = { cache: 'no-cache', method: 'GET', mode: 'cors' }
      const request = new Request(url)
      const response = await fetch(request, options)
      const parsedResponse: ResponseHoliday[] = JSON.parse(await response.text())

      const mappedHolidays = mapHolidays(parsedResponse, filterStore.holidaysByCountry)
      const sortedHolidays = sortHolidays(mappedHolidays)

      filterStore.holidaysByCountry[country].holidays = sortedHolidays
    }
  } catch (error) {
    filterStore.holidayError = true
    handleError({ error, origin: 'fetchHolidays' })
  }
}

export const setHolidayStateSelected = ({
  countryCode,
  stateCode,
  value,
}: {
  countryCode: string
  stateCode: string
  value: boolean
}) => {
  const filterStore = useFilterStore()

  filterStore.holidaysByCountry[countryCode].federalStates[stateCode].selected = value
}

const portDisplayNamesFromPortCodes = (portCodes: string[]) => {
  const filterStore = useFilterStore()
  const ports = filterStore.filterFieldDropDownData.ports

  return portCodes.map(portCode => ports.find(port => port.key === portCode)?.display).filter(Boolean) as string[]
}

export default {
  setFilter,
  stashFilterPreset,
  presetFilterFields,
  preselectFilterEvent,
  fetchFilters,
  mapFilter,
  addFilterSelectTrackingEvent,
  resetFilterSelectionAndRemoveQueryStrings,
  resetFilterSelection,
  setHolidayCountrySelectedByStates,
  setHolidayStateSelectedByCountry,
  fetchHolidays,
  setHolidayStateSelected,
}
